import React, { useEffect, useState, Suspense, lazy } from 'react';
import { CssBaseline, ThemeProvider, Box, CircularProgress } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import MainPage from './pages/MainPage';
import ManagePages from './pages/ManagePages';
import OpenPage from './pages/OpenPage';
import NotesPage from './pages/NotesPage';
import UploadPage from './pages/UploadPage';
import ClientArea from './components/ClientArea';
import HomePage from './components/HomePage';
import MainLayout from './components/MainLayout';
import TestPage from './pages/TestPage';
import SPATestPage from './pages/SPATestPage';
import SPANavigationTest from './pages/SPANavigationTest';
import WhyStillRefreshing from './pages/WhyStillRefreshing';
import VerifyEmail from './pages/VerifyEmail';
import GroupPage from './pages/GroupPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminTestPage from './pages/AdminTestPage';
import AdminManagement from './pages/AdminManagement';
import UserManagement from './pages/UserManagement';
import UserManagementWindow from './pages/UserManagementWindow';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './contexts/AuthContext';
import { GuestIdProvider } from './contexts/GuestIdContext';
import theme from './theme';
import { Toaster } from 'react-hot-toast';
import { scheduleVideoDataIntegrityCheck } from './utils/videoDataIntegrityChecker';

// 🔧 修復：添加錯誤邊界組件
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    // 🔧 修復：忽略螢幕方向變化相關的錯誤
    const errorMessage = error.message.toLowerCase();
    const shouldIgnoreError = 
      errorMessage.includes('rendered more hooks than during the previous render') ||
      errorMessage.includes('rendered fewer hooks than expected') ||
      errorMessage.includes('change in the order of hooks') ||
      errorMessage.includes('rules of hooks') ||
      errorMessage.includes('invalid hook call') ||
      errorMessage.includes('removechild') ||
      errorMessage.includes('node') ||
      errorMessage.includes('cannot read properties') ||
      errorMessage.includes('cannot read property') ||
      errorMessage.includes('undefined is not an object');
    
    if (shouldIgnoreError) {
      console.warn('🔧 檢測到螢幕方向變化相關錯誤，忽略錯誤繼續運行...', error.message);
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🚨 Error Boundary 捕獲到錯誤:', error, errorInfo);
    
    // 🔧 修復：如果是螢幕方向變化相關錯誤，已在 getDerivedStateFromError 中處理
    const errorMessage = error.message.toLowerCase();
    const shouldIgnoreError = 
      errorMessage.includes('rendered more hooks than during the previous render') ||
      errorMessage.includes('rendered fewer hooks than expected') ||
      errorMessage.includes('change in the order of hooks') ||
      errorMessage.includes('rules of hooks') ||
      errorMessage.includes('invalid hook call') ||
      errorMessage.includes('removechild') ||
      errorMessage.includes('node') ||
      errorMessage.includes('cannot read properties') ||
      errorMessage.includes('cannot read property') ||
      errorMessage.includes('undefined is not an object');
    
    if (shouldIgnoreError) {
      return;
    }
    
    // 如果是 DOM 相關錯誤，嘗試恢復應用狀態
    if (error.message.includes('removeChild') || error.message.includes('Node')) {
      console.warn('🔧 檢測到 DOM 錯誤，嘗試恢復應用狀態...');
      
      // 清理可能的 DOM 衝突
      setTimeout(() => {
        try {
          const rootElement = document.getElementById('root');
          if (rootElement) {
            // 清理可能的衝突屬性
            rootElement.removeAttribute('aria-hidden');
            rootElement.removeAttribute('data-reactroot');
          }
        } catch (cleanupError) {
          console.warn('清理 DOM 時發生錯誤:', cleanupError);
        }
      }, 100);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center',
          backgroundColor: '#f5f5f5',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <h2>🚨 應用程式發生錯誤</h2>
          <p>請重新載入頁面以恢復正常功能。</p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '10px'
            }}
          >
            重新載入頁面
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// 擴展ImportMeta類型
interface ImportMetaEnv {
  VITE_GOOGLE_API_KEY: string;
  VITE_GOOGLE_CLIENT_ID: string;
}

interface ImportMeta {
  readonly env: ImportMetaEnv;
}

// 首頁重定向組件 - 重定向到 SPA 模式
const HomeRedirect: React.FC = () => {
  return <Navigate to="/app" replace />;
};

// OAuth 回調組件
const OAuthCallback: React.FC = () => {
  useEffect(() => {
    console.log('🔍 OAuth 回調組件載入');
    console.log('🔍 當前 URL:', window.location.href);
    console.log('🔍 Hash:', window.location.hash);
    console.log('🔍 Search:', window.location.search);
    
    // 檢查是否有 hash 或 search 參數
    const hash = window.location.hash;
    const search = window.location.search;
    
    if (hash || search) {
      console.log('✅ 找到 OAuth 參數，發送消息到父視窗');
      
      // 發送消息到父視窗
      const data = hash ? { hash: hash } : { search: search };
      
      try {
        // 嘗試發送到父視窗
        if (window.opener) {
          window.opener.postMessage(data, window.location.origin);
          console.log('✅ 消息已發送到父視窗');
          
          // 延遲關閉視窗
          setTimeout(() => {
            console.log('🔒 關閉 OAuth 回調視窗');
            window.close();
          }, 1000);
        } else {
          console.warn('⚠️ 沒有父視窗，無法發送消息');
        }
      } catch (error) {
        console.error('❌ 發送消息失敗:', error);
      }
    } else {
      console.warn('⚠️ 沒有找到 OAuth 參數');
    }
  }, []);

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{ textAlign: 'center' }}>
        <h2>正在處理 Google 授權...</h2>
        <p>請稍候，即將關閉此視窗。</p>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [isGapiLoaded, setIsGapiLoaded] = useState(false);
  
  // 路由調試日誌已移除
  
  // 獲取環境變數
  const googleApiKey = import.meta.env.VITE_GOOGLE_API_KEY;
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;


  useEffect(() => {
    // 🔧 暫時禁用影片數據完整性檢查，避免影響工作
    // const timer = setTimeout(() => {
    //   scheduleVideoDataIntegrityCheck().catch(error => {
    //     console.error('❌ 影片數據完整性檢查失敗:', error);
    //   });
    // }, 5000); // 5秒後執行

    const loadGoogleAPI = async () => {
      try {
        console.log('🔍 開始初始化 Google API...');
        console.log('🔍 Google API Key:', googleApiKey ? '已設定' : '未設定');
        console.log('🔍 Google Client ID:', googleClientId ? '已設定' : '未設定');
        
        if (!googleApiKey || !googleClientId) {
          console.warn('⚠️ Google API 配置缺失，跳過 Google API 初始化:', {
            apiKey: !!googleApiKey,
            clientId: !!googleClientId
          });
          console.log('ℹ️ 應用程式將繼續運行，但某些 Google 相關功能可能不可用');
          return;
        }
        
        // 🔧 修復：檢查是否已經載入了 Google API 腳本
        if (document.querySelector('script[src*="apis.google.com"]')) {
          console.log('✅ Google API 腳本已經載入');
        } else {
          const script = document.createElement('script');
          script.src = 'https://apis.google.com/js/api.js';
          script.async = true;
          script.defer = true;
          script.crossOrigin = 'anonymous'; // 添加 CORS 支持
          
          await new Promise<void>((resolve, reject) => {
            script.onload = () => {
              console.log('✅ Google API 腳本載入成功');
              resolve();
            };
            script.onerror = (error) => {
              console.error('❌ Google API 腳本載入失敗:', error);
              reject(new Error('Failed to load Google API script'));
            };
            document.head.appendChild(script); // 添加到 head 而不是 body
          });
        }

        await new Promise<void>((resolve, reject) => {
          (window as any).gapi.load('client:auth2', {
            callback: () => resolve(),
            onerror: () => reject(new Error('Failed to load Google API client'))
          });
        });

        await (window as any).gapi.client.init({
          apiKey: googleApiKey,
          clientId: googleClientId,
          scope: 'https://www.googleapis.com/auth/drive.readonly',
          discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest']
        });

        setIsGapiLoaded(true);
        console.log('✅ Google API initialized successfully');
      } catch (error) {
        console.error('❌ Failed to initialize Google API:', error);
        console.error('💥 錯誤詳情:', {
          message: error instanceof Error ? error.message : 'Unknown error',
          stack: error instanceof Error ? error.stack : 'No stack trace'
        });
      }
    };

    // 🔧 暫時禁用 Google API 初始化，避免 CSP 錯誤影響登入
    // loadGoogleAPI();

    // 清理定時器
    return () => {
      // clearTimeout(timer); // timer 已被註釋掉
    };
  }, [googleApiKey, googleClientId]);

  useEffect(() => {
    const rootElement = document.getElementById('root');
    if (rootElement) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
            // 安全地移除屬性，避免與 React 衝突
            try {
              if (rootElement.hasAttribute('aria-hidden')) {
                rootElement.removeAttribute('aria-hidden');
              }
            } catch (error) {
              console.warn('移除 aria-hidden 屬性時發生錯誤:', error);
            }
          }
        });
      });

      // 使用更安全的觀察選項
      try {
        observer.observe(rootElement, {
          attributes: true,
          attributeFilter: ['aria-hidden'],
          subtree: false // 只觀察根元素，不觀察子元素
        });
      } catch (error) {
        console.warn('設置 MutationObserver 時發生錯誤:', error);
      }

      return () => {
        try {
          observer.disconnect();
        } catch (error) {
          console.warn('斷開 MutationObserver 時發生錯誤:', error);
        }
      };
    }
  }, []);

  console.log("App rendering. Favorite route enabled.");

  // 添加全局錯誤處理器和 DOM 安全包裝器
  useEffect(() => {
    // DOM 安全操作包裝器
    const safeDOMOperation = (operation: () => void, operationName: string) => {
      try {
        operation();
      } catch (error: any) {
        if (error.message && error.message.includes('removeChild')) {
          console.warn(`🔧 DOM 操作 ${operationName} 被安全包裝器捕獲:`, error);
          // 不重新拋出錯誤，讓應用繼續運行
        } else {
          throw error; // 重新拋出非 DOM 錯誤
        }
      }
    };

    // 重寫原生 DOM 方法以增加安全包裝
    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function(child: Node) {
      return safeDOMOperation(() => {
        return originalRemoveChild.call(this, child);
      }, 'removeChild');
    };

    const handleGlobalError = (event: ErrorEvent) => {
      if (event.error && event.error.message && 
          (event.error.message.includes('removeChild') || 
           event.error.message.includes('Node'))) {
        console.warn('🔧 捕獲到全局 DOM 錯誤，嘗試恢復:', event.error);
        event.preventDefault(); // 阻止錯誤冒泡
        
        // 嘗試恢復應用狀態
        setTimeout(() => {
          try {
            const rootElement = document.getElementById('root');
            if (rootElement) {
              rootElement.removeAttribute('aria-hidden');
            }
          } catch (cleanupError) {
            console.warn('全局錯誤恢復失敗:', cleanupError);
          }
        }, 100);
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.reason && event.reason.message && 
          (event.reason.message.includes('removeChild') || 
           event.reason.message.includes('Node'))) {
        console.warn('🔧 捕獲到未處理的 DOM 錯誤 Promise:', event.reason);
        event.preventDefault(); // 阻止錯誤冒泡
      }
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      // 恢復原生方法
      Node.prototype.removeChild = originalRemoveChild;
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <ErrorBoundary>
      <GoogleOAuthProvider 
        clientId={googleClientId}
        onScriptLoadSuccess={() => console.log('Google API 載入成功')}
        onScriptLoadError={() => console.error('Google API 載入失敗')}
      >
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthProvider>
            <GuestIdProvider>
              <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <Routes>
                  <Route path="/" element={<HomeRedirect />} />
                  <Route path="/open" element={<OpenPage />} />
                  <Route path="/openpage-test" element={<OpenPage />} />
                  <Route path="/upload" element={
                    <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>}>
                      <UploadPage />
                    </Suspense>
                  } />
                  <Route path="/client" element={
                    <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>}>
                      <ManagePages />
                    </Suspense>
                  } />
                  <Route path="/favorite" element={<ManagePages />} />
                  <Route path="/manage" element={<ManagePages />} />
                  <Route path="/notes" element={<NotesPage />} />
                  <Route path="/main" element={<MainPage />} />
                  <Route path="/main/:id" element={<MainPage />} />
                  <Route path="/main/share/:id" element={<MainPage />} />
                  <Route path="/v/:id" element={<MainPage />} />
                  <Route path="/share/:id" element={<MainPage />} />
                  <Route path="/s/:shortCode" element={<MainPage />} />
                  <Route path="/sync/:sessionId" element={<MainPage />} />
                  <Route path="/test" element={<TestPage />} />
                  <Route path="/spa-test" element={<SPATestPage />} />
                  <Route path="/spa-nav-test" element={<SPANavigationTest />} />
                  <Route path="/why-refreshing" element={<WhyStillRefreshing />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin-test" element={<AdminTestPage />} />
                  <Route path="/admin-management" element={<AdminManagement />} />
                  <Route path="/user-management" element={<UserManagement />} />
                  <Route path="/user-management-window" element={<UserManagementWindow />} />
                  <Route path="/group/:groupId" element={<GroupPage />} />
                  {/* OAuth 回調路由 */}
                  <Route path="/oauth2callback" element={<OAuthCallback />} />
                  {/* SPA 模式路由 */}
                  <Route path="/app" element={<MainLayout />} />
                </Routes>
              </Router>
              <Toaster position="top-center" />
            </GuestIdProvider>
          </AuthProvider>
        </ThemeProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  );
};

export default App; 